import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import CreateWasteReportModal from '../../components/admin/CreateWasteReportModal';

const WASTE_CATEGORIES = {
  broken: { label: 'שבור/נזק', icon: '💥', color: 'red' },
  lost: { label: 'אבד', icon: '❓', color: 'yellow' },
  expired: { label: 'פג תוקף', icon: '⏰', color: 'orange' },
  damaged: { label: 'פגום', icon: '⚠️', color: 'amber' },
  defective: { label: 'פגם יצרני', icon: '🔧', color: 'purple' },
  other: { label: 'אחר', icon: '📝', color: 'gray' }
};

const AdminWasteReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [pagination.currentPage, selectedCategory, startDate, endDate]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: 20
      };

      if (selectedCategory) params.waste_category = selectedCategory;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get('/api/waste-reports', { params });
      setReports(response.data.reports || []);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('שגיאה בטעינת דוחות בלאי');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get('/api/waste-reports/stats', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDeleteReport = async (report) => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק דוח זה?\nפעולה זו תחזיר ${Math.abs(report.quantity)} יחידות למלאי.`)) {
      return;
    }

    try {
      await axios.delete(`/api/waste-reports/${report.id}`);
      toast.success('הדוח נמחק והמלאי שוחזר');
      fetchReports();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה במחיקת הדוח');
    }
  };

  const handleReportCreated = () => {
    fetchReports();
    fetchStats();
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const filteredReports = searchTerm
    ? reports.filter(r => 
        r.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.product?.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : reports;

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">דוחות בלאי</h1>
            <p className="text-gray-600 mt-1">ניהול דיווחי פגמים, אבדות ונזקים למלאי</p>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors shadow-lg font-semibold"
          >
            <PlusIcon className="h-5 w-5" />
            <span>דוח בלאי חדש</span>
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-3 rounded-lg">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_waste_items || 0}</p>
                  <p className="text-sm text-gray-600">סה"כ פריטי בלאי</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <ChartBarIcon className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">₪{stats.total_financial_loss?.toFixed(0) || 0}</p>
                  <p className="text-sm text-gray-600">הפסד כספי</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <CalendarIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{reports.length || 0}</p>
                  <p className="text-sm text-gray-600">דוחות בתקופה</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <span className="text-2xl">💥</span>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    {stats.waste_by_category?.find(c => c.category === 'broken')?.total_quantity || 0}
                  </p>
                  <p className="text-sm text-gray-600">שבורים</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="חיפוש מוצר..."
              className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">כל הקטגוריות</option>
            {Object.entries(WASTE_CATEGORIES).map(([value, { label, icon }]) => (
              <option key={value} value={value}>
                {icon} {label}
              </option>
            ))}
          </select>

          {/* Start Date */}
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="מתאריך"
          />

          {/* End Date */}
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="עד תאריך"
          />
        </div>

        {(selectedCategory || startDate || endDate || searchTerm) && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              נקה סינונים
            </button>
          </div>
        )}
      </div>

      {/* Reports Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">אין דוחות בלאי</h3>
          <p className="text-gray-600 mb-4">התחל על ידי יצירת דוח בלאי ראשון</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 font-medium"
          >
            <PlusIcon className="h-5 w-5" />
            <span>דוח בלאי חדש</span>
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">תאריך</th>
                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">מק"ט</th>
                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">מוצר</th>
                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">כמות</th>
                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">מחסן</th>
                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">קטגוריה</th>
                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">סיבה</th>
                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase whitespace-nowrap">דיווח ע"י</th>
                <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase whitespace-nowrap">פעולות</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReports.map((report) => {
                const category = WASTE_CATEGORIES[report.waste_category] || WASTE_CATEGORIES.other;
                const quantity = Math.abs(report.quantity);
                
                return (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900">
                      {new Date(report.created_at).toLocaleDateString('he-IL', {
                        day: '2-digit',
                        month: '2-digit'
                      })}
                      <br />
                      <span className="text-[10px] text-gray-500">
                        {new Date(report.created_at).toLocaleTimeString('he-IL', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap">
                      <code className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                        {report.product?.sku || '-'}
                      </code>
                    </td>
                    
                    <td className="px-3 py-3 max-w-[200px]">
                      <div className="text-xs font-semibold text-gray-900 truncate" title={report.product?.name}>
                        {report.product?.name || 'מוצר לא ידוע'}
                      </div>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      <span className="text-base font-bold text-red-600">{quantity}</span>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        report.from_warehouse === 'b2c' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {report.from_warehouse === 'b2c' ? 'B2C' : 'B2B'}
                      </span>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-${category.color}-100 text-${category.color}-800`}>
                        <span className="mr-0.5">{category.icon}</span>
                        {category.label}
                      </span>
                    </td>
                    
                    <td className="px-3 py-3 max-w-[250px]">
                      <div className="text-xs text-gray-700 truncate" title={report.notes}>
                        {report.notes}
                      </div>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-600">
                      {report.performer 
                        ? `${report.performer.first_name} ${report.performer.last_name}`
                        : '-'
                      }
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleDeleteReport(report)}
                        className="text-red-600 hover:text-red-700 font-medium inline-flex items-center gap-1 text-xs"
                        title="מחק ושחזר מלאי"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                מציג {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} עד{' '}
                {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} מתוך {pagination.totalItems}
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                  disabled={pagination.currentPage === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  הקודם
                </button>
                
                <span className="px-4 py-2 text-gray-700 font-medium">
                  עמוד {pagination.currentPage} מתוך {pagination.totalPages}
                </span>
                
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  הבא
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showCreateModal && (
        <CreateWasteReportModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onReportCreated={handleReportCreated}
        />
      )}
    </div>
  );
};

export default AdminWasteReports;
